import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi, useFormSubmit, useAsyncOperation } from '../hooks/useApi';
import { productsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/helpers';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal, { ConfirmModal } from '../components/Modal';
import ProductLoadsModal from '../components/ProductLoadsModal';
import { CubeIcon, PlusIcon, EyeIcon, PencilIcon, TrashIcon, ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';

const ProductsPage = () => {
  const { t } = useTranslation();
  const { isManagerOrAdmin } = useAuth();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLoadsModalOpen, setIsLoadsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  
  const {
    data: products,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh,
  } = usePaginatedApi(productsAPI.getAll);

  // Form submission hooks
  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    productsAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refresh();
      },
      successMessage: t('products.productCreated'),
    }
  );

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => productsAPI.update(selectedProduct.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedProduct(null);
        refresh();
      },
      successMessage: t('products.productUpdated'),
    }
  );

  const { execute, loading: deleteLoading } = useAsyncOperation();

  // Ref to store the debounce timeout ID
  const searchTimeoutRef = useRef(null);

  // Auto-search with debounce when search value changes
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      updateParams({ search });
    }, 300); // 300ms debounce delay

    // Cleanup function to clear timeout
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]); // Only trigger when search value changes

  // Event handlers
  const handleViewLoads = (product) => {
    setSelectedProduct(product);
    setIsLoadsModalOpen(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setIsEditModalOpen(true);
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedProduct) return;
    
    try {
      await execute(
        () => productsAPI.delete(selectedProduct.id),
        {
          showSuccessToast: true,
          successMessage: t('products.productDeleted'),
        }
      );
      setIsDeleteModalOpen(false);
      setSelectedProduct(null);
      refresh();
    } catch (error) {
      // Error is already handled by useAsyncOperation with toast
      // Don't need to do anything here
    }
  };

  // Export products to Excel
  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await productsAPI.export();
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('products.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.message || t('products.exportError'));
    } finally {
      setExportLoading(false);
    }
  };

  // Import products from Excel
  const handleImport = async (file) => {
    try {
      setImportLoading(true);
      const response = await productsAPI.import(file);
      
      const { results } = response.data;
      const message = response.data.message;
      
      // Show detailed results
      if (results.errors.length > 0 || results.skipped.length > 0) {
        let errorMessage = message;
        if (results.errors.length > 0) {
          errorMessage += `\n\nErrors (${results.errors.length}):`;
          results.errors.slice(0, 5).forEach((err) => {
            errorMessage += `\nRow ${err.row}: ${err.message || err.product || 'Unknown error'}`;
          });
          if (results.errors.length > 5) {
            errorMessage += `\n... and ${results.errors.length - 5} more errors`;
          }
        }
        if (results.skipped.length > 0) {
          errorMessage += `\n\nSkipped (${results.skipped.length}):`;
          results.skipped.slice(0, 3).forEach((skip) => {
            errorMessage += `\nRow ${skip.row}: ${skip.product || skip.message}`;
          });
          if (results.skipped.length > 3) {
            errorMessage += `\n... and ${results.skipped.length - 3} more skipped`;
          }
        }
        toast.error(errorMessage, { duration: 10000 });
      } else {
        toast.success(message);
      }
      
      setIsImportModalOpen(false);
      refresh();
    } catch (error) {
      console.error('Import error:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || t('products.importError');
      toast.error(errorMsg, { duration: 8000 });
    } finally {
      setImportLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `${parseFloat(price).toLocaleString()} FCFA`;
  };

  const columns = [
    {
      key: 'name',
      title: t('products.productName'),
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.description && (
            <div className="text-sm text-gray-500">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'unit',
      title: t('products.unit'),
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
          {value}
        </span>
      ),
    },
    {
      key: 'pricePerTon',
      title: t('products.pricePerTon'),
      render: (value) => (
        <span className="font-medium text-gray-900">{formatPrice(value)}</span>
      ),
    },
    {
      key: 'feesPerTonnage',
      title: t('products.feesPerTonnage'),
      render: (value) => (
        <span className="font-medium text-green-600">
          {value ? formatPrice(value) : '-'}
        </span>
      ),
    },
    {
      key: '_count',
      title: t('products.totalLoads'),
      render: (value) => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
          {value?.loads || 0}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: t('tables.date'),
      render: (value) => formatDate(value),
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <Button size="sm" variant="ghost" onClick={() => handleViewLoads(row)} title={t('products.tooltips.viewDetails')}>
            <EyeIcon className="h-4 w-4" />
          </Button>
          {isManagerOrAdmin() && (
            <>
              <Button size="sm" variant="ghost" onClick={() => handleEdit(row)} title={t('products.tooltips.editProduct')}>
                <PencilIcon className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => handleDelete(row)} 
                title={row._count?.loads > 0 ? t('products.tooltips.cannotDeleteWithLoads') : t('products.tooltips.deleteProduct')}
                className={row._count?.loads > 0 ? "text-gray-400 cursor-not-allowed" : "text-red-600 hover:text-red-700"}
                disabled={row._count?.loads > 0}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <CubeIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('products.title')}</h1>
            <p className="text-sm sm:text-base text-gray-600 hidden sm:block">{t('products.subtitle')}</p>
          </div>
        </div>
        
        {isManagerOrAdmin() && (
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={handleExport} disabled={exportLoading} className="flex-1 sm:flex-initial text-xs sm:text-sm">
              <ArrowDownTrayIcon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{exportLoading ? t('common.loading') : t('products.export')}</span>
              <span className="sm:hidden">{t('products.export')}</span>
            </Button>
            <Button variant="outline" onClick={() => setIsImportModalOpen(true)} disabled={importLoading} className="flex-1 sm:flex-initial text-xs sm:text-sm">
              <ArrowUpTrayIcon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('products.import')}</span>
              <span className="sm:hidden">{t('products.import')}</span>
            </Button>
            <Button onClick={() => setIsAddModalOpen(true)} className="flex-1 sm:flex-initial text-xs sm:text-sm">
              <PlusIcon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('products.addProduct')}</span>
              <span className="sm:hidden">{t('products.addProduct')}</span>
            </Button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="mb-4 sm:mb-6">
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md"
          />
        </div>

        <Table
          columns={columns}
          data={products}
          loading={loading}
          pagination={pagination}
          onPageChange={goToPage}
          emptyMessage={t('products.noProductsFound')}
        />
      </div>

      {/* Add Modal */}
      <ProductForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        title={t('products.addNewProduct')}
        submitText={t('common.create')}
      />

      {/* Edit Modal */}
      <ProductForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProduct(null);
        }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        title={t('products.editProductTitle')}
        submitText={t('common.update')}
        initialData={selectedProduct}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedProduct(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('products.deleteProductTitle')}
        message={t('products.deleteConfirm', { name: selectedProduct?.name })}
        confirmText={t('common.delete')}
        variant="danger"
      />

      {/* Product Loads Modal */}
      <ProductLoadsModal
        isOpen={isLoadsModalOpen}
        onClose={() => {
          setIsLoadsModalOpen(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
      />

      {/* Import Modal */}
      <ImportProductsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImport}
        loading={importLoading}
      />
    </div>
  );
};

// Product Form Component
const ProductForm = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  title,
  submitText,
  initialData = {},
}) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(initialData?.id);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unit: 'ton',
    pricePerTon: '',
    feesPerTonnage: ''
  });
  const [errors, setErrors] = useState({});
  const [optionalFieldsOpen, setOptionalFieldsOpen] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      const pricePerTon = initialData?.pricePerTon ?? '';
      const feesPerTonnage = initialData?.feesPerTonnage ?? '';
      setFormData({
        name: initialData?.name || '',
        description: initialData?.description || '',
        unit: initialData?.unit || 'ton',
        pricePerTon,
        feesPerTonnage
      });
      setOptionalFieldsOpen(false);
      setErrors({});
    } else {
      // Reset form when modal closes
      setFormData({
        name: '',
        description: '',
        unit: 'ton',
        pricePerTon: '',
        feesPerTonnage: ''
      });
      setOptionalFieldsOpen(false);
      setErrors({});
    }
  }, [isOpen, initialData?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- initialize product form when opening or switching the edited product

  React.useEffect(() => {
    if (errors.pricePerTon || errors.feesPerTonnage) {
      setOptionalFieldsOpen(true);
    }
  }, [errors.pricePerTon, errors.feesPerTonnage]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('products.form.productNameRequired');
    }
    
    const priceRaw = String(formData.pricePerTon ?? '').trim();
    if (priceRaw && (isNaN(priceRaw) || parseFloat(priceRaw) < 0)) {
      newErrors.pricePerTon = t('products.form.validPriceRequired');
    }
    
    if (formData.feesPerTonnage && (isNaN(formData.feesPerTonnage) || parseFloat(formData.feesPerTonnage) < 0)) {
      newErrors.feesPerTonnage = t('products.form.validFeesRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const priceRaw = String(formData.pricePerTon ?? '').trim();
    const submitData = {
      ...formData,
      feesPerTonnage: formData.feesPerTonnage ? parseFloat(formData.feesPerTonnage) : null
    };
    if (priceRaw) {
      submitData.pricePerTon = parseFloat(priceRaw);
    } else if (isEditMode) {
      delete submitData.pricePerTon;
    } else {
      submitData.pricePerTon = 0;
    }

    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={loading} form="product-form">
            {submitText}
          </Button>
        </>
      }
    >
      <form id="product-form" onSubmit={handleSubmit} className="space-y-4">
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {errors.general}
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Product Name + Unit (same row) */}
          <div className="md:col-span-2 grid grid-cols-2 gap-4">
            <div>
              <Input
                label={t('products.productName')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('products.form.productNamePlaceholder')}
                required
                className={errors.name ? 'border-red-300' : ''}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('products.unit')} *
              </label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="ton">{t('products.form.units.ton')}</option>
                <option value="kg">{t('products.form.units.kg')}</option>
                <option value="m³">{t('products.form.units.m3')}</option>
                <option value="liter">{t('products.form.units.liter')}</option>
                <option value="piece">{t('products.form.units.piece')}</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('products.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('products.form.descriptionPlaceholder')}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Optional pricing — expandable */}
          <details
            className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50/80"
            open={optionalFieldsOpen}
            onToggle={(e) => setOptionalFieldsOpen(e.currentTarget.open)}
          >
            <summary className="cursor-pointer select-none px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100/80 rounded-lg">
              {t('products.form.optionalFieldsSummary')}
            </summary>
            <div className="px-3 pb-3 pt-1 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label={`${t('products.form.pricePerTonLabel')} (${t('common.optional')})`}
                  name="pricePerTon"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.pricePerTon}
                  onChange={handleChange}
                  placeholder={t('products.form.pricePerTonPlaceholder')}
                  className={errors.pricePerTon ? 'border-red-300' : ''}
                />
                {errors.pricePerTon && <p className="text-red-500 text-sm mt-1">{errors.pricePerTon}</p>}
              </div>
              <div>
                <Input
                  label={`${t('products.form.feesPerTonnageLabel')} (${t('common.optional')})`}
                  name="feesPerTonnage"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.feesPerTonnage}
                  onChange={handleChange}
                  placeholder={t('products.form.feesPerTonnagePlaceholder')}
                  className={errors.feesPerTonnage ? 'border-red-300' : ''}
                />
                {errors.feesPerTonnage && <p className="text-red-500 text-sm mt-1">{errors.feesPerTonnage}</p>}
              </div>
            </div>
          </details>
        </div>
      </form>
    </Modal>
  );
};

// Import Products Modal Component
const ImportProductsModal = ({ isOpen, onClose, onImport, loading }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.ms-excel.sheet.macroEnabled.12'
      ];
      
      if (!validTypes.includes(selectedFile.type)) {
        alert(t('products.invalidFileType'));
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) {
      onImport(file);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClose = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('products.importProducts')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('products.selectExcelFile')}
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100
              cursor-pointer"
          />
          <p className="mt-2 text-sm text-gray-500">
            {t('products.importInstructions')}
          </p>
        </div>

        {file && (
          <div className="p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>{t('products.selectedFile')}:</strong> {file.name}
            </p>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!file || loading}>
            {loading ? t('common.loading') : t('products.import')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductsPage;